/**
 * The action body behind the soft person-vs-org shape nudge — the amber "Looks like a PAC" /
 * "Maybe a person — verify." advisory pills. Those pills are derived on every read from the
 * contact's shape (SortInboxService), so switching the contact clears them automatically. But a
 * FALSE POSITIVE — a legitimate single-word organization, or a person legitimately named like an
 * org ("Church") — would otherwise nag forever with no way to say "I checked, it's correct." This
 * body gives the pill that missing action: two mutually exclusive choices.
 *
 *   • "It's a person → switch" reuses the existing shape editor ({@link ContributorShapeBody},
 *     opened on the Person shape) so the treasurer fixes the shared donor (kind + name) exactly as
 *     they do today — no new code path, no regression.
 *   • "It's an organization → dismiss" writes the per-contact ContributorShapeConfirmed audit row
 *     (POST /api/finance/contacts/{id}/confirm-shape). The derive-time read suppresses BOTH shape
 *     nudges for every gift from that donor, and the mutation invalidates the sort inbox so the
 *     pill clears on refetch and stays gone on reload (the audit row IS the dismiss decision).
 */
import { useCallback, useState } from 'react';
import { ContactKind, type TriageItem } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { useConfirmContributorShape } from '@/hooks/queries';
import { ContributorShapeBody } from './ContributorShapeBody';

interface ContributorShapeNudgeBodyProps {
  item: Extract<TriageItem, { kind: 'Contribution' }>;
  /** Collapse the expanded row (the inbox refetch clears the pill). */
  onCollapse: () => void;
}

export function ContributorShapeNudgeBody({ item, onCollapse }: ContributorShapeNudgeBodyProps) {
  const [mode, setMode] = useState<'choose' | 'switch'>('choose');
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: confirmShapeAsync, isPending } = useConfirmContributorShape();

  const name = item.contribution.contactName ?? 'this donor';

  const dismiss = useCallback(async () => {
    if (isPending) return;
    setError(null);
    try {
      await confirmShapeAsync({ contactId: item.contribution.contactId });
      onCollapse();
    } catch {
      setError('Could not dismiss the reminder. Please try again.');
    }
  }, [isPending, confirmShapeAsync, item.contribution.contactId, onCollapse]);

  // "It's a person" hands off to the SAME shape editor the ledger already uses, opened on the
  // Person shape so the treasurer lands on the type they're switching to.
  if (mode === 'switch') {
    return (
      <ContributorShapeBody
        item={item}
        contactConcurrencyVersion={item.contribution.contactConcurrencyVersion}
        onClassified={onCollapse}
        initialShape={ContactKind.Individual}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        We flagged{' '}
        <span className="font-medium text-foreground">{name}</span> because the name and the
        recorded contributor type might not match. If it's really a person, switch it and add a
        last name. If the name is correct as a single-word organization, dismiss this reminder —
        we won't ask again for this donor.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => setMode('switch')}>
          It's a person → switch
        </Button>
        <Button type="button" onClick={() => void dismiss()} disabled={isPending}>
          {isPending ? 'Dismissing…' : "It's an organization → dismiss"}
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
